import InputSymphony from "elements/InputSymphony"
import ModalSymphony, { ModalSymphonyProps } from "elements/ModalSymphony"
import TabsSymphony, { TabItem } from "elements/TabsSymphony"
import TabSymphony from "elements/TabSymphony"
import useCampaignBuilder from "Hooks/useCampaignBuilder"
import { PROMOTABLE_ITEMS, PromotableItem } from "modules/Campaign"
import { AdConnectionType, PlaybookStepName, PlaybookType, PromotableItemTypes } from "modules/Const"
import { RecordDetails, RecordSelectorType } from "modules/Types"
import { clsx, makeKey } from "modules/Utils"
import PlatformStartButton from "pages/MarketingPage/Components/CreateNewCampaignModal/PlatformStartButton"
import { CSSProperties, useEffect, useState } from "react"
import RecordSearchView from "elements/RecordSearchView"
import ConnectionStep from "pages/MarketingPage/Components/StepsSocialMedia/ConnectionStep"
import Icon from "elements/Icon"
import useFacebookConnections from "Hooks/useFacebookConnections"
import ButtonSymphony from "elements/ButtonSymphony"
import AdCreativeSelectorCampaign from "./CampaignViews/AdCreativeSelectorCampaignView"
import AdPreviewCampaignView, { getPreviewTabOptions } from "./CampaignViews/AdPreviewCampaignView"
import { MaestroLoadingMessage } from "pages/MarketingPage/MaestroBanner"
import AdConnectionModal from "./shareable/AdConnectionModal"
import { CURRENCY_CODE_DATA } from "helpers/currencies"
import millify from "millify"
import Colors from "modules/Colors"
import { useHistory } from "react-router-dom"
import useMaestroChatStore from "Hooks/useMaestroChat"
import Banner from "elements/Banner"
import LinkSelectorCampaignView from "./CampaignViews/LinkSelectorCampaignView"

const ONE_CLICK_TABS: TabItem[] = [
    {
        id: PromotableItemTypes.MUSIC,
        title: 'Music',
    },
    {
        id: PromotableItemTypes.POSTS,
        title: 'Socials',
    },
    {
        id: PromotableItemTypes.WEBSITES,
        title: 'Signups & Traffic',
    },
    {
        id: PromotableItemTypes.EVENT,
        title: 'Events & Tours',
    },
]

interface OneClickCampaignSetupModalProps {
    className?: string
}

const OneClickCampaignSetupModal = ({
    className,
}: OneClickCampaignSetupModalProps) => {
    const history = useHistory()
    const {
        showOneClickCampaignSetupModal,
        setShowOneClickCampaignSetupModal,
        setShowOneClickCampaignModal,
        getInstagramPosts,
        getReleases,
        getTikTokPosts,
        getYouTubeVideos,
        instagramPosts,
        releases,
        tikTokPosts,
        youTubeVideos,
        setSelectedSongToPromote,
        setSelectedInstagramPost,
        setSelectedRelease,
        setSelectedTikTokPost,
        setSelectedYouTubeVideo,
        setExpressSetupOrCustomPages,
        conversionsEventName,
        generateOneClickCampaign
    } = useCampaignBuilder()

    const {
        loggedInFacebookUser,
        isLoggingIntoFacebook,
        loginWithFacebook: loginWithFB,
        selectedFacebookAdAccount,
        selectedFacebookPixel,
        selectedFacebookPage,
        selectedInstagramPage,
    } = useFacebookConnections()

    const {
        setIsChatOpen,
    } = useMaestroChatStore()

    const [selectedOneClickTab, setSelectedOneClickTab] = useState<TabItem>(ONE_CLICK_TABS[0])

    const CONTENT_TABS = getPreviewTabOptions('facebook')
    const [selectedContentTab, setSelectedContentTab] = useState<TabItem>(CONTENT_TABS[0]);
    const [modalStep, setModalStep] = useState<PlaybookStepName>(PlaybookStepName.PROMOTABLE)
    const [selectedPromotableItem, setSelectedPromotableItem] = useState<PromotableItem | null>(null)
    const [searchQuery, setSearchQuery] = useState<string>('');

    // Ad Connections Modal
    const [showAdConnectionsModal, setShowAdConnectionsModal] = useState(false)
    const [selectedAdConnectionType, setSelectedAdConnectionType] = useState<AdConnectionType | null>(null)

    useEffect(() => {
        switch (selectedOneClickTab.id) {
            case 'instagram':
                getInstagramPosts()
                break
            case 'release':
                getReleases()
                break
            case 'tiktok':
                getTikTokPosts()
                break
            case 'youtube':
                getYouTubeVideos()
                break
        }
    }, [selectedOneClickTab])


    useEffect(() => {
        if (selectedAdConnectionType) {
            setShowAdConnectionsModal(true)
        } else {
            setShowAdConnectionsModal(false)
        }
    }, [selectedAdConnectionType])

    useEffect(() => {
        if (modalStep === PlaybookStepName.ONE_CLICK_CAMPAIGN_GENERATION) {
            generateOneClickCampaign()
        }
    }, [modalStep])

    const handleCloseModal = () => {
        setShowOneClickCampaignSetupModal(false)
        setShowOneClickCampaignModal(false)
        setModalStep(PlaybookStepName.PROMOTABLE)
        setSelectedOneClickTab(ONE_CLICK_TABS[0])
        setSelectedPromotableItem(null)
    }

    const renderOneClickTabs = () => {
        return (
            <TabsSymphony
                className="oneClickCampaignSetupModalTabs"
                tabs={ONE_CLICK_TABS}
                selectedTab={selectedOneClickTab}
                onChange={(tab) => setSelectedOneClickTab(tab as TabItem)}
            />
        )
    }

    const renderInstagramTab = () => {
        return (
            <TabSymphony
                className="oneClickCampaignSetupModalInstagramTab"
                visible={selectedOneClickTab.id === 'instagram'}
            >
                {instagramPosts.map((post, index) => (
                    <div
                        key={makeKey(post.id, index)}
                        onClick={() => {
                            setSelectedInstagramPost(post)
                            setSelectedRelease(null)
                            setSelectedTikTokPost(null)
                            setSelectedYouTubeVideo(null)
                            setShowOneClickCampaignSetupModal(false)
                            setShowOneClickCampaignModal(true)
                        }}
                    >
                        <img src={post.media_url} alt={post.caption} />
                        <div className="oneClickCampaignSetupModalInstagramTabCaption">
                            {post.caption}
                        </div>
                    </div>
                ))}
            </TabSymphony>
        )
    }

    const renderReleaseTab = () => {
        return (
            <TabSymphony
                className="oneClickCampaignSetupModalReleaseTab"
                visible={selectedOneClickTab.id === 'release'}
            >
                {releases.map((release, index) => (
                    <div
                        key={makeKey(release.id, index)}
                        onClick={() => {
                            setSelectedRelease(release)
                            setSelectedInstagramPost(null)
                            setSelectedTikTokPost(null)
                            setSelectedYouTubeVideo(null)
                            setShowOneClickCampaignSetupModal(false)
                            setShowOneClickCampaignModal(true)
                        }}
                    >
                        <div className="oneClickCampaignSetupModalReleaseTabTitle">
                            my release {index}
                        </div>
                    </div>
                ))}
            </TabSymphony>
        )
    }

    const renderTikTokTab = () => {
        return (
            <TabSymphony
                className="oneClickCampaignSetupModalTikTokTab"
                visible={selectedOneClickTab.id === 'tiktok'}
            >
                {tikTokPosts.map((post, index) => (
                    <div
                        key={makeKey(post.id, index)}
                        onClick={() => {
                            setSelectedTikTokPost(post)
                            setSelectedInstagramPost(null)
                            setSelectedRelease(null)
                            setSelectedYouTubeVideo(null)
                            setShowOneClickCampaignSetupModal(false)
                            setShowOneClickCampaignModal(true)
                        }}
                    >
                        <div className="oneClickCampaignSetupModalTikTokTabTitle">
                            {post.title}
                        </div>
                    </div>
                ))}
            </TabSymphony>
        )
    }

    const renderYouTubeTab = () => {
        return (
            <TabSymphony
                className="oneClickCampaignSetupModalYouTubeTab"
                visible={selectedOneClickTab.id === 'youtube'}
            >
                {youTubeVideos.map((video, index) => (
                    <div
                        key={makeKey(video.id, index)}
                        onClick={() => {
                            setSelectedYouTubeVideo(video)
                            setSelectedInstagramPost(null)
                            setSelectedRelease(null)
                            setSelectedTikTokPost(null)
                            setShowOneClickCampaignSetupModal(false)
                            setShowOneClickCampaignModal(true)
                        }}
                    >
                        <div className="oneClickCampaignSetupModalYouTubeTabTitle">
                            {video.title}
                        </div>
                    </div>
                ))}
            </TabSymphony>
        )
    }

    if (!showOneClickCampaignSetupModal) {
        return null
    }


    const renderUrlEntryInput = () => {
        return (
            <InputSymphony
                className="runInstagramAdsSearchInput"
                placeholder="Enter a URL to promote"
                onChange={(value) => {
                    setSearchQuery(value)
                }}
                value={searchQuery}

            />
        )
    }


    const handlePromotableItemClick = (promotableItem: PromotableItem) => {
        setSelectedPromotableItem(promotableItem)
        switch (promotableItem.playbookType) {
            case PlaybookType.INCREASE_MUSIC_STREAMS:
                setModalStep(PlaybookStepName.SONG_SELECTION)
                break
            case PlaybookType.GROW_PLAYLIST_FOLLOWERS:
                setModalStep(PlaybookStepName.PLAYLIST_SELECTION)
                break
            case PlaybookType.META_EMAIL_SIGNUPS:
                setModalStep(PlaybookStepName.EMAIL_LIST_SELECTION)
                break
            case PlaybookType.META_PRESAVES:
                setModalStep(PlaybookStepName.PRESAVES_SELECTION)
                break
            case PlaybookType.META_IG_SOUND:
                setModalStep(PlaybookStepName.IG_SOUND_SELECTION)
                break
            case PlaybookType.TIKTOK_SOUND:
                setModalStep(PlaybookStepName.TIKTOK_SOUND_SELECTION)
                break
            case PlaybookType.INCREASE_LINK_CLICKS:
                setModalStep(PlaybookStepName.LINK_ENTRY)
                break
        }
    }

    const renderPromotableView = () => {
        return (
            <div className="oneClickCampaignSetupModalPromotableView">
                {/* {renderUrlEntryInput()} */}
                <ButtonSymphony
                    className="oneClickCampaignSetupModalHelpMePickButton"
                    variant="text"
                    onClick={() => {
                        setIsChatOpen(true)
                    }}
                >
                    Help me pick
                </ButtonSymphony>
                {/* <TabsSymphony
                    className="oneClickCampaignSetupModalTabs"
                    isBorderBottom={true}
                    style={{
                        marginBottom: 8,
                    }}
                    tabs={ONE_CLICK_TABS}
                    selectedTab={selectedOneClickTab}
                    onChange={(tab) => setSelectedOneClickTab(tab as TabItem)}
                /> */}
                <div className="oneClickCampaignSetupModalPromotableItems2" style={sty.oneClickCampaignSetupModalPromotableItems}>
                    {PROMOTABLE_ITEMS
                        .filter((promotableItem) => promotableItem.type === selectedOneClickTab.id)
                        .map((promotableItem) => {
                            const { title, icon, description } = promotableItem;
                            return (
                                <PlatformStartButton
                                    onClick={() => handlePromotableItemClick(promotableItem)}
                                    title={title}
                                    description={description}
                                    icon={icon}
                                    fullWidth={true}
                                    size="large"
                                />
                            )
                        })}
                </div>
            </div>
        )
    }

    const renderLeftSideContent = () => {
        return (
            <div className="oneClickCampaignModalLeftSideContent" style={sty.oneClickCampaignModalLeftSideContent}>
                <TabsSymphony
                    className="oneClickCampaignModalLeftSideContentTabs"
                    tabs={CONTENT_TABS}
                    selectedTab={selectedContentTab.id}
                    onChange={(tab) => setSelectedContentTab(tab as TabItem)}
                    style={{
                        backgroundColor: 'white',
                        width: 'fit-content',
                        borderRadius: 1000,
                        paddingLeft: 16,
                        paddingRight: 16,
                    }}
                />
                <div className="oneClickCampaignModalAdPreview" style={sty.oneClickCampaignModalAdPreview}>
                    <AdPreviewCampaignView
                        variant="facebook"
                        hidePlatformSelector={true}
                        hideTitle={true}
                        hideSubtitle={true}
                        overridePreviewType={selectedContentTab.id as 'ig-story' | 'ig-reels' | 'ig-feed'}
                    />
                </div>
            </div>
        )
    }

    const renderSongSelection = () => {
        let allowedOptions: RecordSelectorType[] = []
        let defaultOption: RecordSelectorType | undefined

        if (selectedPromotableItem?.playbookType === PlaybookType.INCREASE_MUSIC_STREAMS) {
            allowedOptions = ['single', 'project', 'playlist']
            defaultOption = 'single'
        } else if (selectedPromotableItem?.playbookType === PlaybookType.GROW_PLAYLIST_FOLLOWERS) {
            allowedOptions = ['playlist']
            defaultOption = 'playlist'
        }

        const handleNextStep = () => {
            // if user is logged in, check if they have a connected ad account and page
            // if they do, go to the CREATIVE SELECTION step
            // if they don't, go to the CONNECTION SELECTION step
            let nextStep = PlaybookStepName.CONNECTION_DECISION
            if (loggedInFacebookUser) {
                if (selectedFacebookAdAccount && selectedFacebookPage) {
                    nextStep = PlaybookStepName.CREATIVE_SELECTION
                } else {
                    nextStep = PlaybookStepName.LOGIN_TO_PAGES
                }
            }
            setModalStep(nextStep)
        }
        return (
            <div>
                <RecordSearchView
                    className="recordSelectorTabRecordSearchView"
                    handleSelectRecord={(recordDetails: RecordDetails) => {
                        setSelectedSongToPromote(recordDetails)
                        handleNextStep()

                    }}
                    options={allowedOptions}
                    letUserSearchSpotify
                    letUserRefreshReleases={false}
                />
            </div>
        )
    }

    const renderConnectionDecision = () => {

    
        return (
            <div>
                <ConnectionStep
                    hideLineIndicator={true}
                    stepTitle="Connect your FB + IG Pages"
                    stepIcon={<Icon className="expressSetupStepIcon" icon="facebook" size={40} />}
                    connectedAccountName={loggedInFacebookUser?.name || 'Login with Facebook to get started'}
                    connectedAccountIcon={
                        <Icon
                            className="expressSetupStepIcon"
                            icon={loggedInFacebookUser?.picture?.data.url || 'facebook'}
                            iconType={loggedInFacebookUser ? 'custom' : 'default'}
                            size={40}
                        />
                    }
                    isCompleted={Boolean(loggedInFacebookUser)}
                    isLoading={isLoggingIntoFacebook}
                    onButtonClick={loginWithFB}
                />
            </div>
        )
    }

    const renderConnectionSelection = () => {
        // Define the connection steps
        let selectedAdAccountCurrency = selectedFacebookAdAccount?.currency || 'USD'
        let selectedAdAccountCurrencyName = CURRENCY_CODE_DATA[selectedAdAccountCurrency]?.name_plural || 'United States Dollar'

        const connectionSteps = [
            {
                stepTitle: 'Ad Account',
                connectedAccountName: selectedFacebookAdAccount?.name || 'Connect your Ad Account',
                connectedAccountIcon: <Icon
                    className="expressSetupStepIcon"
                    icon={'facebook'}
                    size={40} />,
                stepDescription: 'Your Facebook ad account is where you want Symphony to publish your ads. You\'ll be billed based on the billing method in your ad account.',
                isCompleted: Boolean(selectedFacebookAdAccount),
                onButtonClick: () => {
                    setSelectedAdConnectionType(AdConnectionType.FACEBOOK_AD_ACCOUNT)
                },
                connectedAccountDescriptions: selectedFacebookAdAccount ? [
                    `$${millify(selectedFacebookAdAccount?.amount_spent || 0)} spent`,
                    `This ad account uses the currency ${selectedAdAccountCurrencyName} (${selectedAdAccountCurrency}).`
                ] : undefined,
                canEdit: true,
                hideLineIndicator: true
            },
            {
                stepTitle: 'Facebook + Instagram Pages',
                connectedAccountName: selectedFacebookPage?.name || 'Connect your Facebook Page',
                connectedAccountIcon: <Icon
                    className="expressSetupStepIcon"
                    icon={selectedFacebookPage?.picture?.data.url || 'facebook'}
                    iconType={selectedFacebookPage ? 'custom' : 'default'}
                    size={40} />,
                stepDescription: 'Select the Facebook Page and Instagram Page you want to use for your ads.',
                isCompleted: Boolean(selectedFacebookPage),
                onButtonClick: () => {
                    setSelectedAdConnectionType(AdConnectionType.FACEBOOK_PAGE)
                },
                canEdit: true,
                iconContainerColor: Colors.grey,
                isLastStep: !Boolean(selectedFacebookPage && selectedInstagramPage),
                hideLineIndicator: true
            }
        ]

        // Add Instagram page step if Facebook page is connected
        // if (selectedFacebookPage) {
        //     connectionSteps.push({
        //         stepTitle: 'Instagram Page',
        //         connectedAccountName: selectedInstagramPage?.instagramAccount?.username || 'Connect your Instagram Page',
        //         connectedAccountIcon: <Icon
        //             className="expressSetupStepIcon"
        //             icon={selectedInstagramPage?.instagramAccount?.profile_pic || 'instagram'}
        //             iconType={selectedInstagramPage ? 'custom' : 'default'}
        //             size={40} />,
        //         stepDescription: 'This Instagram page will be the one seen by Instagram ad viewers. Your Instagram page needs to be connected to the Facebook page you\'ve linked above.',
        //         isCompleted: Boolean(selectedInstagramPage),
        //         onButtonClick: () => {
        //             setSelectedAdConnectionType(AdConnectionType.FACEBOOK_PAGE)
        //         },
        //         isLastStep: Boolean(selectedFacebookPage && selectedInstagramPage)
        //     })
        // }

        // Add pixel connection step
        // connectionSteps.push({
        //     stepTitle: 'Enable Conversions for immediate impact',
        //     connectedAccountName: selectedFacebookPixel?.name || 'Connect your Facebook Pixel',
        //     connectedAccountIcon: <Icon
        //         className="expressSetupStepIcon"
        //         icon={'facebook'}
        //         iconType={'default'}
        //         size={40} />,
        //     stepDescription: '(Optional) Improve your ad performance by adding your Facebook Pixel for better results.',
        //     isCompleted: Boolean(selectedFacebookPixel && selectedFacebookPixel?.id),
        //     onButtonClick: () => {
        //         setSelectedAdConnectionType(AdConnectionType.FACEBOOK_PIXEL)
        //     },
        //     canEdit: true,
        //     iconContainerColor: Colors.grey,
        //     isLastStep: !Boolean(selectedFacebookPage && selectedInstagramPage),
        //     hideLineIndicator: true
        // })

        // Handle conversion event settings
        let selectedConversionEventName = conversionsEventName
        let canEditConversionEvent = true
        let conversionEventIcon = 'facebook'
        let stepTitle = 'Conversion Event'

        // if (playbookType === PlaybookType.INCREASE_MUSIC_STREAMS ||
        //     playbookType === PlaybookType.GROW_PLAYLIST_FOLLOWERS
        // ) {
        //     selectedConversionEventName = 'Ads will optimize to maximize streams & followers.'
        //     canEditConversionEvent = false
        //     conversionEventIcon = 'boost'
        //     stepTitle = 'Conversion Event'
        // }

        // Add conversion event step if pixel is connected
        // if (selectedFacebookPixel?.id) {
        //     connectionSteps.push({
        //         stepTitle: stepTitle,
        //         connectedAccountName: selectedConversionEventName || 'Select the Conversion Event',
        //         connectedAccountIcon: <Icon
        //             isCircle={!canEditConversionEvent}
        //             className="expressSetupStepIcon"
        //             icon={conversionEventIcon}
        //             iconType={'default'}
        //             size={40} />,
        //         stepDescription: 'Choose which conversion event you want to optimize your ads for.',
        //         isCompleted: Boolean(selectedConversionEventName),
        //         onButtonClick: () => {
        //             setSelectedAdConnectionType(AdConnectionType.FACEBOOK_CONVERSION_EVENT)
        //         },
        //         canEdit: canEditConversionEvent,
        //         iconContainerColor: Colors.grey,
        //     })
        // }

        return (
            <div>
                {connectionSteps.map((step, index) => (
                    <ConnectionStep
                        key={`connection-step-${index}`}
                        hideLineIndicator={step.hideLineIndicator}
                        stepTitle={step.stepTitle}
                        stepIcon={step.connectedAccountIcon}
                        connectedAccountName={step.connectedAccountName}
                        stepDescription={step.stepDescription}
                        isCompleted={step.isCompleted}
                        isLoading={false}
                        onButtonClick={step.onButtonClick}
                        canEdit={step.canEdit}
                        isLastStep={step.isLastStep}
                        connectedAccountDescriptions={step.connectedAccountDescriptions}
                    />
                ))}
            </div>
        )
    }
    const renderCreativeSelection = () => {
        return (
            <div>
                <AdCreativeSelectorCampaign
                    variant="facebook"
                    hideTitle={true}
                    hideSubtitle={true}
                    className="oneClickCampaignSetupModalCreativeSelectorCampaign"
                />
            </div>
        )
    }

    const renderOneClickCampaignGeneration = () => {
        return (
            <div>
                <MaestroLoadingMessage startLoading={true} />
                <ButtonSymphony
                    className="oneClickCampaignSetupModalGenerateButton"
                    onClick={() => generateOneClickCampaign()}
                    variant="outlined"
                >Generate</ButtonSymphony>
            </div>
        )
    }

    const renderPresavesSelection = () => {
        return (
            <div className="oneClickCampaignSetupModalPresavesSelection" style={sty.oneClickCampaignSetupModalPresavesSelection}>
                <div>
                    <Banner
                        className="oneClickCampaignSetupModalPresavesBanner"
                        color="maestro"
                        title="Get more fans with Symphony Presaves"
                    >
                        <p>Symphony Presaves help you grow faster with 1-click presaves, email collection, Forever Saves, and full presave customization.</p>
                        <ButtonSymphony
                            className="oneClickCampaignSetupModalPresavesButton"
                            onClick={() => {
                                handleCloseModal()
                                history.push('/marketing/new/presave')
                            }}
                            variant="primary"
                            style={{
                                marginTop: 8,
                            }}
                            iconLeft={<Icon
                                className="oneClickCampaignSetupModalPresavesButtonIcon"
                                icon="boost"
                                color={Colors.white}
                                size={24} />}
                        >Create a new presave campaign</ButtonSymphony>
                    </Banner>
                </div>
                <ButtonSymphony
                    className="oneClickCampaignSetupModalPresavesButton"
                    onClick={() => setModalStep(PlaybookStepName.CREATIVE_SELECTION)}
                    variant="outlined"
                >Promote an existing presave link</ButtonSymphony>
            </div>
        )
    }

    const renderEmailListSelection = () => {
        return (
            <div className="oneClickCampaignSetupModalPresavesSelection" style={sty.oneClickCampaignSetupModalPresavesSelection}>
                <div>
                    <Banner
                        className="oneClickCampaignSetupModalPresavesBanner"
                        color="maestro"
                        title="Get more fans with Symphony Presaves"
                    >
                        <p>Symphony Presaves help you grow faster with 1-click presaves, email collection, Forever Saves, and full presave customization.</p>
                        <ButtonSymphony
                            className="oneClickCampaignSetupModalPresavesButton"
                            onClick={() => {
                                handleCloseModal()
                                history.push('/marketing/new/presave')
                            }}
                            variant="primary"
                            style={{
                                marginTop: 8,
                            }}
                            iconLeft={<Icon
                                className="oneClickCampaignSetupModalPresavesButtonIcon"
                                icon="boost"
                                color={Colors.white}
                                size={24} />}
                        >Create a new Email Collection Page</ButtonSymphony>
                    </Banner>
                </div>
                <ButtonSymphony
                    className="oneClickCampaignSetupModalPresavesButton"
                    onClick={() => setModalStep(PlaybookStepName.CREATIVE_SELECTION)}
                    variant="outlined"
                >Promote an existing landing page</ButtonSymphony>
            </div>
        )
    }

    const renderLinkEntry = () => {
        return (
            <div>
                <LinkSelectorCampaignView />
            </div>
        )
    }

    const getPropsForModalStep = (): Partial<ModalSymphonyProps> & { renderContent: () => JSX.Element } => {
        switch (modalStep) {
            case PlaybookStepName.PROMOTABLE:
                return {
                    title: 'What do you want to promote?',
                    subtitle: 'Create an AI-powered ad campaign to grow anything.',
                    renderContent: renderPromotableView,
                }
            case PlaybookStepName.SONG_SELECTION:
                return {
                    title: 'Select a Song or Album',
                    subtitle: 'Choose a song or album to promote',
                    renderContent: renderSongSelection,
                    buttonLeftText: 'Back',
                    onClickButtonLeft: () => setModalStep(PlaybookStepName.PROMOTABLE),
                    buttonLeftVariant: 'outlined',
                }
            case PlaybookStepName.PRESAVES_SELECTION:
                return {
                    title: 'Do you already have a presave campaign?',
                    subtitle: 'You can promote an existing presave, or make a new presave using Symphony\'s Presave toolset.',
                    renderContent: renderPresavesSelection,
                    buttonLeftText: 'Back',
                    onClickButtonLeft: () => setModalStep(PlaybookStepName.PROMOTABLE),
                    buttonLeftVariant: 'outlined',
                }

            case PlaybookStepName.EMAIL_LIST_SELECTION:
                return {
                    title: 'Where do you want to collect emails?',
                    subtitle: 'You can collect emails from your website, or use a third-party email list.',
                    renderContent: renderEmailListSelection,
                    buttonLeftText: 'Back',
                    onClickButtonLeft: () => setModalStep(PlaybookStepName.PROMOTABLE),
                    buttonLeftVariant: 'outlined',
                }

            case PlaybookStepName.CONNECTION_DECISION:

                let buttonRightText = 'Skip this step'
                let buttonRightModalStep = PlaybookStepName.CREATIVE_SELECTION
                if (loggedInFacebookUser) {
                    buttonRightText = 'Next'
                    buttonRightModalStep = PlaybookStepName.LOGIN_TO_PAGES
                } else {
                    buttonRightText = 'Skip this step'
                    buttonRightModalStep = PlaybookStepName.CREATIVE_SELECTION
                }
                return {
                    title: 'Do you want to connect your accounts?',
                    subtitle: 'Logging in with your own pages will let you grow followers + maximize ad performance by using your audiences.',
                    renderContent: renderConnectionDecision,
                    onClickButtonLeft: () => setModalStep(PlaybookStepName.PROMOTABLE),
                    buttonLeftText: 'Back',
                    buttonLeftVariant: 'outlined',
                    buttonRightText: buttonRightText,
                    onClickButtonRight: () => setModalStep(buttonRightModalStep),
                }
            case PlaybookStepName.LOGIN_TO_PAGES:
                return {
                    title: 'Select the pages you want to use for ads.',
                    subtitle: 'Connect your Facebook and Instagram accounts to get started.',
                    renderContent: renderConnectionSelection,
                    buttonRightText: 'Skip this step',
                    buttonLeftText: 'Back',
                    buttonLeftVariant: 'outlined',
                    onClickButtonLeft: () => setModalStep(PlaybookStepName.CONNECTION_DECISION),
                    onClickButtonRight: () => setModalStep(PlaybookStepName.CREATIVE_SELECTION),
                }

            case PlaybookStepName.LINK_ENTRY:
                return {
                    title: 'Enter the link you want to promote.',
                    subtitle: 'Your ads will send high-quality traffic to the link you enter.',
                    renderContent: renderLinkEntry,
                    buttonLeftText: 'Back',
                    onClickButtonLeft: () => setModalStep(PlaybookStepName.PROMOTABLE),
                    buttonLeftVariant: 'outlined',
                }
            case PlaybookStepName.CREATIVE_SELECTION:


                return {
                    title: "Which assets do you want to use for your ad?",
                    subtitle: "Choose from a variety of assets to create a campaign that will grow your audience.",
                    renderContent: renderCreativeSelection,
                    buttonLeftText: 'Back',
                    onClickButtonLeft: () => setModalStep(PlaybookStepName.CONNECTION_DECISION),
                    buttonLeftVariant: 'outlined',
                    leftSideContent: renderLeftSideContent(),
                    buttonRightText: 'Next',
                    onClickButtonRight: () => setModalStep(PlaybookStepName.ONE_CLICK_CAMPAIGN_GENERATION),
                }
            case PlaybookStepName.ONE_CLICK_CAMPAIGN_GENERATION:
                return {
                    title: 'Creating the best campaign for you...',
                    subtitle: 'Pulling together the best campaign settings for you based on your selections.',
                    leftSideContent: renderLeftSideContent(),
                    renderContent: renderOneClickCampaignGeneration,
                }
            default:
                return {
                    title: 'One Click Campaign Setup',
                    subtitle: 'What do you want to promote?',
                    renderContent: renderUrlEntryInput,
                }
        }
    }

    const {
        title,
        subtitle,
        renderContent,
        buttonLeftText,
        onClickButtonLeft,
        buttonLeftVariant,
        leftSideContent,
        buttonRightText,
        onClickButtonRight,
    } = getPropsForModalStep()

    // NOTE: render()
    return (
        <>
            <ModalSymphony
                className={clsx(
                    'oneClickCampaignSetupModal',
                    className
                )}
                isOpen={showOneClickCampaignSetupModal}
                onClose={handleCloseModal}
                title={title}
                size={"large"}
                subtitle={subtitle}
                buttonLeftText={buttonLeftText}
                onClickButtonLeft={onClickButtonLeft}
                buttonLeftVariant={buttonLeftVariant}
                leftSideContent={leftSideContent}
                buttonRightText={buttonRightText}
                onClickButtonRight={onClickButtonRight}
                isIsolateModalContent={true}
            >
                <TabsSymphony
                    className="oneClickCampaignSetupModalTabs"
                    isBorderBottom={true}
                    style={{
                        marginBottom: 8,
                    }}
                    tabs={ONE_CLICK_TABS}
                    selectedTab={selectedOneClickTab}
                    onChange={(tab) => setSelectedOneClickTab(tab as TabItem)}
                />
                <div className="oneClickCampaignSetupModalContent"
                    style={sty.oneClickCampaignSetupModalContent}>
                    {renderContent()}
                </div>
            </ModalSymphony>
            <AdConnectionModal
                isOpen={showAdConnectionsModal}
                onClose={() => setSelectedAdConnectionType(null)}
                connectionType={selectedAdConnectionType || undefined}
            />
        </>
    )
}

const sty: Record<string, CSSProperties> = {
    oneClickCampaignSetupModalPromotableItems: {
        display: 'flex',
        flexDirection: 'column',
        gap: 16,
    },
    oneClickCampaignSetupModalContent: {
        marginBottom: 24,
        marginLeft: 'auto',
        marginRight: 'auto',
        width: '100%',
        backgroundColor: Colors.greyLight,
        padding: 16,
    },
    oneClickCampaignModalLeftSideContent: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, rgba(111, 66, 193, 0.9) 0%, rgba(72, 149, 239, 0.8) 100%)',
        height: '100%',
        padding: 24,
        boxShadow: 'inset 0px 0px 20px 0px rgba(0, 0, 0, 0.5)',
        gap: 24,
        borderBottomLeftRadius: 16,
        borderTopLeftRadius: 16,
    },
    oneClickCampaignModalAdPreview: {
        width: 280,
        height: 450,
        backgroundColor: 'white',
        borderRadius: 16,
        boxShadow: '0px 0px 20px 0px rgba(0, 0, 0, 0.5)',
    },
    oneClickCampaignSetupModalPresavesSelection: {
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
    }
}

export default OneClickCampaignSetupModal